import { useState, useEffect } from 'react';
import CookieConsent from 'react-cookie-consent';
import Cookies from 'js-cookie';
import styles from './styles.module.css';
import { useModuleSettingsByName } from '../../../global-hooks';
import { setRerenderKey } from '../../../global-state/redux/actions';
import { useDispatch } from 'react-redux';
import { emptyNull } from '../../../shared/misc';

export const ACCEPTED_ALL = 'ACCEPTED_ALL';
export const ACCEPTED_NECESSARY = 'ACCEPTED_NECESSARY';

const CookieOption = ({ title, description, checked, onChange, locked }) => (
  <div className={styles.option}>
    <label className={styles.label}>
      <input
        type="checkbox"
        checked={checked}
        readOnly={locked}
        onChange={onChange}
        className={styles.switchNative}
        aria-label={title}
      />
      <span className={`${styles.switch}${locked ? ` ${styles.locked}` : ''}`} />
      <span className={styles.optionTitle}>{title}</span>
    </label>
    <p className={styles.optionDesc}>{description}</p>
  </div>
);

const CookieBanner = () => {
  const moduleSettings = useModuleSettingsByName('cookieBanner') ?? emptyNull;
  const {
    cookieName = 'vsx_cookie',
    expires = 150,
    text: {
      generalText = 'This site uses cookies. Some are essential; others help us improve your experience.',
      cookieSettingsText = 'Cookie settings',
      acceptNecessaryText = 'Accept only necessary cookies',
      acceptCustomSettingsText = 'Accept custom settings',
      acceptAllText = 'Accept all cookies',
      necessaryCookiesTitleText = 'Necessary cookies',
      necessaryCookiesDescriptionText = 'These cookies enable core functionality. The site cannot function properly without them.',
      analyticsCookiesTitleText = 'Analytics cookies',
      analyticsCookiesDescriptionText = 'Analytics cookies help us improve the website by collecting and reporting information on usage.'
    } = emptyNull,
    styles: {
      wrapperStyles = emptyNull,
      buttonPrimaryStyles = emptyNull,
      buttonSecondaryStyles = emptyNull,
      buttonTertiaryStyles = emptyNull
    } = emptyNull
  } = moduleSettings;

  const [visible, setVisible] = useState(!Cookies.get(cookieName));
  const [panelOpen, setPanelOpen] = useState(false);
  const [analyticsAllowed, setAnalyticsAllowed] = useState(true);
  const dispatch = useDispatch();

  useEffect(() => {
    setVisible(!Cookies.get(cookieName));
  }, [cookieName]);

  if (!visible) return null;

  const saveConsent = (val) => {
    Cookies.set(cookieName, val, {
      expires,
      path: '/',
      sameSite: 'Lax',
      secure: window.location.protocol === 'https:'
    });
    dispatch(setRerenderKey());
    setVisible(false);
    window.dispatchEvent(new Event('cookieConsentUpdated'));
  };

  const acceptCustom = () => saveConsent(analyticsAllowed ? ACCEPTED_ALL : ACCEPTED_NECESSARY);
  const togglePanel = () => setPanelOpen((o) => !o);

  return (
    <CookieConsent
      ButtonComponent={() => null}
      disableStyles={false}
      overlay={false}
      containerClasses={`VSX-CookieConsent-Container ${styles.wrapper}`}
      contentStyle={wrapperStyles}
      cookieName={cookieName}
      expires={expires}
    >
      <div className={styles.row}>
        <div className={styles.text}>{generalText}</div>
        <div className={styles.buttons}>
          <button
            type="button"
            className={styles.linkBtn}
            style={buttonTertiaryStyles}
            onClick={togglePanel}
            aria-label={cookieSettingsText}
          >
            {cookieSettingsText}
          </button>
          <button
            type="button"
            className={styles.btnSecondary}
            style={buttonSecondaryStyles}
            onClick={() => saveConsent(ACCEPTED_NECESSARY)}
            aria-label={acceptNecessaryText}
          >
            {acceptNecessaryText}
          </button>
          <button
            type="button"
            className={styles.btnPrimary}
            style={buttonPrimaryStyles}
            onClick={() => saveConsent(ACCEPTED_ALL)}
            aria-label={acceptAllText}
          >
            {acceptAllText}
          </button>
        </div>
      </div>

      {panelOpen && (
        <>
          <hr className={styles.divider} />
          <div className={styles.panel}>
            <CookieOption
              title={necessaryCookiesTitleText}
              description={necessaryCookiesDescriptionText}
              checked
              locked
            />
            <CookieOption
              title={analyticsCookiesTitleText}
              description={analyticsCookiesDescriptionText}
              checked={analyticsAllowed}
              onChange={(e) => setAnalyticsAllowed(e.target.checked)}
            />
          </div>
          <div className={styles.panelButtons}>
            <button
              type="button"
              className={styles.btnPrimary}
              style={buttonPrimaryStyles}
              onClick={acceptCustom}
              aria-label={acceptCustomSettingsText}
            >
              {acceptCustomSettingsText}
            </button>
          </div>
        </>
      )}
    </CookieConsent>
  );
};

export default CookieBanner;
